import { useMemo } from "react";
import useQueryParam from "@hooks/useQueryParam";
import { RESTAURANT_SEGMENT_LABELS } from "@pages/Home/sections/HomePlaces/constants";
import { getState } from "@enums";
import { TerritoryOption } from "@apiHooks/useTerritoryOptions";
import { iChainsFilters } from "@models/ChainsFilters";
import { LocationsTab } from "../NewTopChains";

export interface UrlChainsFilters {
    initialFilters: Partial<iChainsFilters>;
    initialLocationsTab: LocationsTab;
    isReady: boolean;
}

export const toArray = (raw: string | null): string[] =>
    (raw ?? '').split(',').map((v) => v.trim()).filter(Boolean);

/**
 * Reads ?segments, ?onSiteSegments, ?states, and ?metros URL params (each a
 * single comma-separated key, e.g. ?states=CA,TX) and translates them into
 * initialFilters suitable for seeding ChainsFilters.
 *
 * @param territoryOptions - pass the useTerritoryOptions metros array when
 *   metros params are present; pass null to skip metro resolution (isReady
 *   will be true immediately). This avoids a conditional hook call in the
 *   parent component.
 */
function useUrlChainsFilters(
    territoryOptions: TerritoryOption[] | null,
): UrlChainsFilters {
    const segmentParams = toArray(useQueryParam('segments'));
    const onSiteSegmentParams = toArray(useQueryParam('onSiteSegments'));
    const stateParams = toArray(useQueryParam('states'));
    const metroParams = toArray(useQueryParam('metros'));
    const includeParams = toArray(useQueryParam('includes'));
    const excludeParams = toArray(useQueryParam('excludes'));

    const initialFilters = useMemo<Partial<iChainsFilters>>(() => {
        const result: Partial<iChainsFilters> = {};

        if (includeParams.length > 0) {
            result.includes = includeParams;
        }
        if (excludeParams.length > 0) {
            result.excludes = excludeParams;
        }

        const translatedSegments = segmentParams
            .map((key) => RESTAURANT_SEGMENT_LABELS[key as keyof typeof RESTAURANT_SEGMENT_LABELS])
            .filter((label): label is string => Boolean(label));

        const allSegments = Array.from(
            new Set([...translatedSegments, ...onSiteSegmentParams])
        );
        if (allSegments.length > 0) {
            result.segments = allSegments;
        }

        const translatedStates = stateParams
            .map((abbr) => getState(abbr.toUpperCase()))
            .filter((name): name is string => name !== '');
        if (translatedStates.length > 0) {
            result.states = translatedStates;
        }

        if (metroParams.length > 0 && territoryOptions !== null) {
            const metroIdToLabel = new Map(
                territoryOptions.map((opt) => [opt.id, opt.label])
            );
            const resolvedMetros = metroParams
                .map((id) => metroIdToLabel.get(id))
                .filter((label): label is string => Boolean(label));
            if (resolvedMetros.length > 0) {
                result.metros = resolvedMetros;
            }
        }

        return result;
    }, [segmentParams, onSiteSegmentParams, stateParams, metroParams, includeParams, excludeParams, territoryOptions]);

    const initialLocationsTab = useMemo<LocationsTab>(
        () => metroParams.length > 0 && stateParams.length === 0 ? 'metros' : 'states',
        [metroParams.length, stateParams.length]
    );

    const isReady = metroParams.length === 0 || territoryOptions !== null;

    return { initialFilters, initialLocationsTab, isReady };
}

export default useUrlChainsFilters;
